import React from "react";
import classes from "./GlobalOverviewResponse.module.css";

const NOT_CALCULABLE = "Not calculable";
const EMPTY_VALUE = "—";

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 20,
});
const percentageFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const isMissing = (value) => value === null || value === undefined;

const formatNumericValue = (value, formatter, fallback = EMPTY_VALUE) => {
  if (isMissing(value)) return fallback;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? formatter.format(numericValue) : String(value);
};

const formatPercentage = (value, signed = false) => {
  if (isMissing(value)) return NOT_CALCULABLE;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);

  const prefix = signed && numericValue > 0 ? "+" : "";
  return `${prefix}${percentageFormatter.format(numericValue)}%`;
};

export const formatGlobalOverviewValue = (value, type) => {
  switch (type) {
    case "integer":
      return formatNumericValue(value, integerFormatter);
    case "number":
      return formatNumericValue(value, numberFormatter);
    case "percentage":
      return formatPercentage(value);
    case "signed_number": {
      if (isMissing(value)) return EMPTY_VALUE;

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return String(value);

      const prefix = numericValue > 0 ? "+" : "";
      return `${prefix}${numberFormatter.format(numericValue)}`;
    }
    case "signed_percentage":
      return formatPercentage(value, true);
    case "text":
    default:
      return isMissing(value) ? EMPTY_VALUE : String(value);
  }
};

export const hasGlobalOverviewTableRows = (table) =>
  Array.isArray(table?.rows) && table.rows.length > 0;

const ReasonActualChangeCell = ({ value }) => {
  if (isMissing(value)) return EMPTY_VALUE;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  if (numericValue === 0) return "0";

  const isIncrease = numericValue > 0;
  const magnitude = integerFormatter.format(Math.abs(numericValue));
  const formattedValue = `${isIncrease ? "+" : "-"}${magnitude}`;

  return (
    <span
      className={classes.directionalChange}
      aria-label={`${isIncrease ? "Increase" : "Decrease"}: ${magnitude}`}
    >
      <span aria-hidden="true">{isIncrease ? "\u25b2" : "\u25bc"}</span>{" "}
      <span>{formattedValue}</span>
    </span>
  );
};

const isNumericColumn = (type) =>
  [
    "integer",
    "number",
    "percentage",
    "signed_number",
    "signed_percentage",
  ].includes(type);

const GlobalOverviewTable = ({ table }) => {
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  const rows = Array.isArray(table?.rows) ? table.rows : [];

  if (!columns.length || !rows.length) return null;

  const tableLabel = table?.title || "Global overview data";

  return (
    <div
      className={classes.tableScroll}
      role="region"
      aria-label={`${tableLabel} table`}
      tabIndex={0}
    >
      <table className={classes.table} aria-label={tableLabel}>
        <thead>
          <tr>
            {columns.map((column, columnIndex) => {
              const numeric = isNumericColumn(column?.type);

              return (
                <th
                  key={column?.key || columnIndex}
                  scope="col"
                  className={numeric ? classes.numeric : undefined}
                >
                  {column?.label ?? column?.key ?? ""}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row?.id || row?.key || rowIndex}>
              {columns.map((column, columnIndex) => {
                const numeric = isNumericColumn(column?.type);
                const value = row?.[column?.key];
                const isReasonActualChange =
                  table?.id === "reason_changes" &&
                  column?.key === "actual_change";

                return (
                  <td
                    key={column?.key || columnIndex}
                    className={numeric ? classes.numeric : undefined}
                  >
                    {isReasonActualChange ? (
                      <ReasonActualChangeCell value={value} />
                    ) : (
                      formatGlobalOverviewValue(value, column?.type)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GlobalOverviewTable;
